import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import MoleculeLoader from '../components/MoleculeLoader';
import { CheckCircle2, MailX, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UnsubscribePage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // 'subscribed', 'unsubscribed'
  const [processing, setProcessing] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!email || !token) {
      setError('Invalid unsubscribe link');
      setLoading(false);
      return;
    }
    fetch(`${API_URL}/api/newsletter/status?email=${encodeURIComponent(email)}&token=${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error || data.detail) {
          setError(data.error || data.detail);
        } else {
          setStatus(data.unsubscribed ? 'unsubscribed' : 'subscribed');
          setName(data.name || '');
        }
      })
      .catch(() => setError('Unable to check subscription status'))
      .finally(() => setLoading(false));
  }, [email, token]);

  const handleUnsubscribe = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token })
      });
      if (res.ok) setStatus('unsubscribed');
      else {
        const data = await res.json();
        setError(data.detail || 'Failed to unsubscribe');
      }
    } catch {
      setError('Failed to unsubscribe');
    } finally {
      setProcessing(false);
    }
  };

  const handleResubscribe = async () => {
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/resubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token })
      });
      if (res.ok) setStatus('subscribed');
      else {
        const data = await res.json();
        setError(data.detail || 'Failed to resubscribe');
      }
    } catch {
      setError('Failed to resubscribe');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <MoleculeLoader size={72} label="Loading subscription preferences…" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
        <MailX className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-900 mb-2">Error</h2>
        <p className="text-slate-500">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        {status === 'unsubscribed' ? (
          <>
            <MailX className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-testid="unsub-title">Unsubscribed</h2>
            <p className="text-slate-500 mb-2">
              {name ? `${name}, you` : 'You'} have been unsubscribed from Genora Peptides article notifications.
            </p>
            <p className="text-sm text-slate-400 mb-6">
              You will still receive order confirmations and important account emails.
            </p>
            <Button
              onClick={handleResubscribe}
              disabled={processing}
              variant="outline"
              className="border-teal-600 text-teal-700 hover:bg-teal-50"
              data-testid="resubscribe-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              {processing ? 'Processing...' : 'Resubscribe'}
            </Button>
          </>
        ) : (
          <>
            <Mail className="h-16 w-16 text-teal-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-testid="sub-title">
              {name ? `Hi ${name}` : 'Newsletter Preferences'}
            </h2>
            <p className="text-slate-500 mb-2">
              You are currently subscribed to Genora Peptides article notifications.
            </p>
            <p className="text-sm text-slate-400 mb-6">
              Unsubscribing only stops article emails. You will still receive order confirmations and important account notifications.
            </p>
            <Button
              onClick={handleUnsubscribe}
              disabled={processing}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="unsubscribe-btn"
            >
              <MailX className="h-4 w-4 mr-2" />
              {processing ? 'Processing...' : 'Unsubscribe from Articles'}
            </Button>
          </>
        )}

        {status === 'subscribed' && (
          <div className="mt-6 pt-4 border-t border-slate-100">
            <CheckCircle2 className="h-5 w-5 text-green-500 inline mr-1" />
            <span className="text-sm text-green-600">Currently subscribed</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnsubscribePage;
